import axios from "axios";
import { loginStart, loginSuccess, loginFailure, logout } from "./authSlice";
import { jwtDecode } from "jwt-decode";
import { requestForToken } from "../../firebase-config";
 
// Helper function to safely access localStorage
const safeLocalStorage = {
  setItem: (key, value) => {
    try {
      if (typeof Storage !== "undefined" && window.localStorage) {
        localStorage.setItem(key, value);
        return true;
      }
    } catch (error) {
      console.warn("localStorage setItem failed:", error);
    }
    return false;
  },
 
  getItem: (key) => {
    try {
      if (typeof Storage !== "undefined" && window.localStorage) {
        return localStorage.getItem(key);
      }
    } catch (error) {
      console.warn("localStorage getItem failed:", error);
    }
    return null;
  },
 
  removeItem: (key) => {
    try {
      if (typeof Storage !== "undefined" && window.localStorage) {
        localStorage.removeItem(key);
        return true;
      }
    } catch (error) {
      console.warn("localStorage removeItem failed:", error);
    }
    return false;
  },
 
  clear: () => {
    try {
      if (typeof Storage !== "undefined" && window.localStorage) {
        localStorage.clear();
        return true;
      }
    } catch (error) {
      console.warn("localStorage clear failed:", error);
    }
    return false;
  },
};
 
// Login action creator
export const loginUser = (identifier, password, userType) => async (dispatch) => {
  dispatch(loginStart());
 
  const getApiUrl = () => {
    switch (userType) {
      case "admin":
        return `${process.env.REACT_APP_SITE_API_URL}admin/login/`;
      case "employee":
        return `${process.env.REACT_APP_SITE_API_URL}employee/login/`;
      case "manager":
        return `${process.env.REACT_APP_SITE_API_URL}manager/login/`;
      default:
        return `${process.env.REACT_APP_SITE_API_URL}admin/login/`;
    }
  };
 
  try {
    const response = await axios.post(getApiUrl(), {
      identifier: identifier.trim(),
      password: password.trim(),
    });
 
    if (response.data && response.status === 200) {
      // Store tokens safely
      const accessStored = safeLocalStorage.setItem("accessToken", response.data.access);
      const refreshStored = safeLocalStorage.setItem("refreshToken", response.data.refresh);
 
      if (!accessStored || !refreshStored) {
        console.warn("Failed to store tokens in localStorage");
      }
 
      // Decode token
      const decodedToken = jwtDecode(response.data.access);
 
      // Update Redux store
      dispatch(
        loginSuccess({
          userId: decodedToken.user_id,
          userRole: decodedToken.user_type,
          fullName: decodedToken.full_name,
        })
      );
 
      // Register push notifications
      await requestForToken();
 
      return { success: true };
    } else {
      dispatch(loginFailure("Invalid response from server"));
      return { success: false, error: "Invalid response from server" };
    }
  } catch (error) {
    let errorMessage = "Login failed";
    if (error.response) {
      if (error.response.status === 401) {
        errorMessage = "Invalid credentials";
      } else if (error.response.status === 404) {
        errorMessage = "Account not found";
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.request) {
      errorMessage = "Server is not responding";
    }
 
    dispatch(loginFailure(errorMessage));
    return { success: false, error: errorMessage };
  }
};
 
 
// Enhanced logout action creator
export const logoutUser = () => (dispatch) => {
  try {
    // Clear tokens from localStorage with error handling
    const accessRemoved = safeLocalStorage.removeItem("accessToken");
    const refreshRemoved = safeLocalStorage.removeItem("refreshToken");
 
    if (!accessRemoved || !refreshRemoved) {
      console.warn("Failed to remove tokens from localStorage");
      // Try to clear all localStorage as fallback
      safeLocalStorage.clear();
    }
 
    // Dispatch logout action
    dispatch(logout());
 
    // Force a small delay to ensure state update
    setTimeout(() => {
      // Additional cleanup if needed
      // console.log("Logout completed");
    }, 100);
  } catch (error) {
    console.error("Logout error:", error);
    // Still dispatch logout even if localStorage fails
    dispatch(logout());
  }
};
 
// Check if user is already logged in based on stored token
export const checkAuthState = () => (dispatch) => {
  const token = safeLocalStorage.getItem("accessToken");
 
  if (token) {
    try {
      // Decode token to get user information
      const decodedToken = jwtDecode(token);
 
      // Check if token is expired
      const currentTime = Date.now() / 1000;
      if (decodedToken.exp < currentTime) {
        // Token expired, logout
        dispatch(logoutUser());
        return false;
      }
 
      // Token valid, update auth state
      dispatch(
        loginSuccess({
          userId: decodedToken.user_id,
          userRole: decodedToken.user_type,
          fullName: decodedToken.full_name,
        })
      );
 
      return true;
    } catch (error) {
      console.error("Token validation error:", error);
      // Invalid token, logout
      dispatch(logoutUser());
      return false;
    }
  }
 
  return false;
};
 
// Additional utility function to force logout (for debugging)
export const forceLogout = () => (dispatch) => {
  try {
    // Clear all possible storage
    safeLocalStorage.clear();
 
    // Also try direct localStorage clear as backup
    if (window.localStorage) {
      window.localStorage.removeItem("accessToken");
      window.localStorage.removeItem("refreshToken");
    }
 
    // Dispatch logout
    dispatch(logout());
 
    // Force page reload as last resort (uncomment if needed)
    // window.location.reload();
  } catch (error) {
    console.error("Force logout error:", error);
    dispatch(logout());
  }
};
 
 